<template>
  <a-auto-complete
    v-bind="attrs"
    v-model:value="innerValue"
    :open="open"
    :class="[bem.b(), bem.is(isGray ? 'gray-auto-complete' : '')]"
    :filter-option="filterOption"
    :options="options"
    @select="onSelect"
    @blur="handleBlur"
    @focus="handleFocus"
  >
    <!-- 自定义插槽 -->
    <template
      v-for="item in Object.keys($slots).filter((name) => name !== 'default')"
      #[item]="data"
    >
      <slot :name="item" v-bind="data"></slot>
    </template>

    <!-- 默认插槽 -->
    <slot>
      <ll-input
        v-if="!isMultiLine"
        :allow-clear="allowClear"
        :placeholder="placeholder"
        :bordered="bordered"
        :is-gray="isGray"
        @press-enter="(e) => handleKeyDown(e, 'input')"
      />
      <ll-textarea
        v-else
        :allow-clear="allowClear"
        :placeholder="placeholder"
        :is-gray="isGray"
        :bordered="bordered"
        @press-enter="(e) => handleKeyDown(e, 'textarea')"
      />
    </slot>
  </a-auto-complete>
</template>

<script setup lang="ts">
import { createNamespace } from '@ll-plus/utils'
import { computed, ref, useAttrs } from 'vue'
import { autoCompleteProps } from './auto-complete'

defineOptions({ name: 'LlAutoComplete' })

const attrs = useAttrs()
const bem = createNamespace('auto-complete')

const props = defineProps(autoCompleteProps)
const emits = defineEmits(['update:value', 'change', 'blur', 'focus', 'select'])

const isBlur = ref(true)

const innerValue = computed({
  get() {
    return props.value || ''
  },
  set(val) {
    emits('update:value', val)
    emits('change', val)
  },
})

const open = computed(() => {
  if (isBlur.value && props.expandOpportunity !== 'focus') return false

  if (props.expandOpportunity === 'character') {
    if (!props.triggerCharacter || isBlur.value) return false
    const result = props.triggerCharacter
      .split(/[,，\s]+/)
      .some((char) => innerValue.value.endsWith(char))

    return result
  }
  // 输入框有值
  else if (props.expandOpportunity === 'change') {
    if (isBlur.value) return false
    return !!innerValue.value
  }
  // 聚焦
  else {
    return undefined
  }
})

// 解析过滤函数
const newFilterOption = computed(() => {
  // 处理布尔值情况
  if (typeof props.filterOption === 'boolean') {
    return props.filterOption
      ? defaultFilterOption // 使用默认过滤函数
      : () => true // 禁用过滤，始终返回 true
  }

  // 处理函数情况
  if (typeof props.filterOption === 'function') {
    return props.filterOption
  }

  // 默认情况使用内置过滤
  return defaultFilterOption
})

// 默认过滤函数（支持大小写配置）
const defaultFilterOption = (input: string, option: { value: string }) => {
  // 处理大小写转换
  const compare = (str: string) =>
    props.caseSensitive ? str : str.toUpperCase()

  return compare(option.value).includes(compare(input))
}

// 最终的过滤函数
const filterOption = (input: string, option: { value: string }) => {
  return newFilterOption.value(input, option)
}

// 处理键盘事件
const handleKeyDown = (e: KeyboardEvent, type: string) => {
  if (e.key === 'Enter' && type === 'textarea') {
    if (e.shiftKey) {
      // Shift + Enter: 允许换行，不触发选中
      // 阻止事件冒泡，避免触发 AutoComplete 的默认选中行为
      e.stopPropagation()
      // 允许默认的换行行为
    } else {
      // 单独的 Enter: 触发选中，阻止换行
      e.preventDefault()
    }
  }

  if (props.ignoreSelection && e.key === 'Enter' && innerValue.value) {
    if (
      !props.options!.some((item) =>
        String(item.value).includes(String(innerValue.value)),
      )
    ) {
      e.stopPropagation()
    }
  }
}
const onSelect = (value: string) => {
  if (props.appendMode) {
    // 补齐的后缀
    const suffix = props.appendCharacter + value

    // 若之前有相同值，阻止选中
    if (innerValue.value.includes(suffix)) {
      innerValue.value = innerValue.value?.trim() || ''
    } else {
      innerValue.value = `${innerValue.value}${innerValue.value.trim().endsWith(props.appendCharacter) ? '' : props.appendCharacter}${value}`
    }
  } else {
    innerValue.value = value
  }
  emits('select', value)
}

const handleBlur = () => {
  isBlur.value = true
  emits('blur')
}
const handleFocus = () => {
  isBlur.value = false
  emits('focus')
}
</script>
